import { Badge, Subheading, SurfaceCard, SurfaceRow } from '../../../components/ui'
import type { ComponentProps } from 'react'
import { SYMPTOM_CATEGORIES, DAILY_CHECKINS, SYMPTOM_LOG } from '../constants'
import { uiClasses } from '../../shared/uiClasses'
import { GradientSphereMetricCard, sphereColorAt } from '../components/GradientSphereCard'

const TEXT_PRIMARY = uiClasses.textPrimary
const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_SUBTLE = uiClasses.textSubtle
type BadgeColor = ComponentProps<typeof Badge>['variant']
type SymptomCategory = 'Energy' | 'Mood' | 'Digestion' | 'Pain' | 'Sleep Quality' | 'Stress'
type SymptomMetricKey = 'energy' | 'mood' | 'digestion' | 'pain' | 'sleepQuality' | 'stress'
type DailyCheckin = (typeof DAILY_CHECKINS)[number]

const CATEGORY_KEYS: Record<SymptomCategory, SymptomMetricKey> = {
  Energy: 'energy',
  Mood: 'mood',
  Digestion: 'digestion',
  Pain: 'pain',
  'Sleep Quality': 'sleepQuality',
  Stress: 'stress',
}

function scoreColor(score: number): BadgeColor {
  if (score >= 7) return 'emerald'
  if (score >= 5) return 'amber'
  return 'red'
}

function scoreBg(score: number): string {
  if (score >= 7) return 'dash-viz-score-high'
  if (score >= 5) return 'dash-viz-score-mid'
  return 'dash-viz-score-low'
}

function scoreBgMuted(score: number): string {
  if (score >= 7) return 'dash-viz-score-high-soft'
  if (score >= 5) return 'dash-viz-score-mid-soft'
  return 'dash-viz-score-low-soft'
}

function categoryAverage(key: SymptomMetricKey): number {
  const total = DAILY_CHECKINS.reduce((sum, checkin) => sum + checkin[key], 0)
  return +(total / DAILY_CHECKINS.length).toFixed(1)
}

function ScaleBar({ value }: { value: number }) {
  const color = scoreBg(value)
  const muted = scoreBgMuted(value)

  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 10 }, (_, index) => (
        <span
          key={index}
          className={`inline-block h-2.5 w-3 rounded-sm ${index < value ? color : muted}`}
        />
      ))}
    </div>
  )
}

export default function SymptomsTab() {
  const symptomCategories = SYMPTOM_CATEGORIES as SymptomCategory[]

  const categoryAverages = symptomCategories.map((category) => {
    const key = CATEGORY_KEYS[category]
    return {
      category,
      key,
      value: categoryAverage(key),
    }
  })

  const latestCheckin = DAILY_CHECKINS[0]
  const highSeverityLogs = SYMPTOM_LOG.filter((entry) => entry.severity >= 4).length

  return (
    <div className="space-y-6">
      <SurfaceCard as="article" className="symptoms-overview-shell">
        <div className="symptoms-overview-layout">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Subheading variant="dashboard">Symptoms pattern board</Subheading>
              <Badge variant="zinc">
                {DAILY_CHECKINS.length} daily check-ins
              </Badge>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Badge variant="zinc">
                {highSeverityLogs} high-severity log entries
              </Badge>
              <Badge variant="zinc">
                Latest stress score {latestCheckin.stress}/10
              </Badge>
            </div>
          </div>

          <div className="symptoms-avg-grid">
            {categoryAverages.map((average, index) => (
              <GradientSphereMetricCard
                key={average.category}
                label={average.category}
                value={average.value}
                sphereColor={sphereColorAt(index)}
              />
            ))}
          </div>
        </div>
      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Subheading variant="dashboard">Daily check-ins</Subheading>
          <Badge variant="zinc">7-day stream</Badge>
        </div>

        <div className="symptoms-checkin-stream mt-4">
          {DAILY_CHECKINS.map((checkin) => (
            <SurfaceRow key={checkin.id}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className={`w-16 shrink-0 text-sm font-medium ${TEXT_PRIMARY}`}>{checkin.date}</span>

                <div className="flex items-center gap-2">
                  {symptomCategories.map((category) => {
                    const key = CATEGORY_KEYS[category]
                    const value = (checkin as DailyCheckin)[key]
                    return (
                      <span key={category} className="flex items-center gap-0.5">
                        <span className={`inline-block size-2 rounded-full ${scoreBg(value)}`} />
                        <span className={`text-xs tabular-nums ${TEXT_SECONDARY}`}>{value}</span>
                      </span>
                    )
                  })}
                </div>

                {checkin.notes ? <span className={`text-xs/5 ${TEXT_SUBTLE}`}>- {checkin.notes}</span> : null}
              </div>
            </SurfaceRow>
          ))}
        </div>
      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Subheading variant="dashboard">Symptom log</Subheading>
          <Badge variant="zinc">{SYMPTOM_LOG.length} entries</Badge>
        </div>

        <div className="symptoms-log-stream mt-4">
          {SYMPTOM_LOG.map((entry) => (
            <SurfaceRow key={entry.id}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs/5 ${TEXT_SUBTLE}`}>{entry.date}</span>
                    <span className={`text-sm font-semibold ${TEXT_PRIMARY}`}>{entry.symptom}</span>
                    <Badge variant={scoreColor(10 - entry.severity)}>
                      Trigger: {entry.trigger}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`text-xs/5 ${TEXT_SUBTLE}`}>Severity</span>
                    <ScaleBar value={entry.severity} />
                    <span className={`text-xs font-medium tabular-nums ${TEXT_SECONDARY}`}>
                      {entry.severity}/10
                    </span>
                  </div>

                  <div className={`text-xs/5 ${TEXT_SUBTLE}`}>
                    Duration: <span className={TEXT_SECONDARY}>{entry.duration}</span>
                  </div>

                  {entry.notes ? <p className={`text-xs/5 ${TEXT_SUBTLE}`}>{entry.notes}</p> : null}
                </div>
              </div>
            </SurfaceRow>
          ))}
        </div>
      </SurfaceCard>
    </div>
  )
}
